// ---------------------------------------------------------------------------
// Graph User
// ---------------------------------------------------------------------------

import { z } from 'zod';

export const graphUserSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  userPrincipalName: z.string(),
  userType: z.string().optional().nullable().default(null),
  /**
   * Sélectionné pour que l'appelant puisse *distinguer* un compte parti : la
   * recherche ne les rend qu'aux admins, et un résultat qu'on ne peut pas
   * marquer visuellement serait un piège plutôt qu'une information.
   */
  accountEnabled: z.boolean().optional().nullable().default(null),
  givenName: z.string().optional().nullable().default(null),
  surname: z.string().optional().nullable().default(null),
  jobTitle: z.string().optional().nullable().default(null),
  department: z.string().optional().nullable().default(null),
  officeLocation: z.string().optional().nullable().default(null),
  mail: z.string().optional().nullable().default(null),
  mobilePhone: z.string().optional().nullable().default(null),
  employeeId: z.string().optional().nullable().default(null),
  companyName: z.string().optional().nullable().default(null),
  onPremisesDistinguishedName: z.string().optional().nullable().default(null),
});

export type GraphUser = z.infer<typeof graphUserSchema>;

export type FindUserResult =
  | { found: true; user: GraphUser }
  | { found: false; reason: 'not_found' | 'guest' };

/** Comma-separated $select string for Graph API user queries — derived from graphUserSchema. */
export const graphUserSelectParam = Object.keys(graphUserSchema.shape).join(
  ',',
);

// ---------------------------------------------------------------------------
// CASL — Roles & Capabilities
// ---------------------------------------------------------------------------

import type { MongoAbility, MongoQuery, SubjectRawRule } from '@casl/ability';
import { createMongoAbility } from '@casl/ability';

export type AppSubject =
  | 'Photo'
  | 'Card'
  | 'Template'
  | 'Side'
  | 'User'
  | 'Batch'
  | 'Audit'
  | 'Dashboard'
  | 'all';

export type AppAction =
  | 'read'
  | 'create'
  | 'update'
  | 'delete'
  | 'generate'
  | 'upload'
  | 'manage'
  | 'moderate'
  | 'export';

export type AppAbility = MongoAbility<[AppAction, AppSubject]>;

// ── Roles ────────────────────────────────────────────────────────────────────

export type AppRole = 'user' | 'operator' | 'admin';

export const Roles = {
  USER: 'user',
  OPERATOR: 'operator',
  ADMIN: 'admin',
} as const;

// A tuple, not `AppRole[]`: `z.enum(VALID_ROLES)` needs one, and it narrows.
export const VALID_ROLES = [
  Roles.USER,
  Roles.OPERATOR,
  Roles.ADMIN,
] as const satisfies readonly AppRole[];

/**
 * Narrow `value` to one of `values` — the typed replacement for
 * `(values as readonly string[]).includes(value)` followed by `value as T`.
 * `.some` with `===` accepts any `value` without widening `values`.
 */
export function isOneOf<const T>(
  values: readonly T[],
  value: unknown,
): value is T {
  return values.some((v) => v === value);
}

/** Capabilities CASL générées pour chaque rôle (hiérarchique : chaque niveau inclut le précédent). */
export const ROLE_CAPABILITIES: Record<AppRole, string[]> = {
  user: [
    'read:Photo',
    'upload:Photo',
    // self only — enforced by controller (delete reserved to operator+)
  ],
  operator: [
    'read:Photo',
    'upload:Photo',
    'delete:Photo',
    'manage:Photo',
    'moderate:Photo',
    'generate:Card',
    'upload:Batch',
  ],
  admin: [
    'read:Photo',
    'upload:Photo',
    'delete:Photo',
    'manage:Photo',
    'moderate:Photo',
    'generate:Card',
    'upload:Batch',
    'read:Template',
    'create:Template',
    'update:Template',
    'delete:Template',
    'create:Side',
    'update:Side',
    'delete:Side',
    'read:User',
    'create:User',
    'update:User',
    'read:Audit',
    'read:Dashboard',
    // Not on Photo: an operator's manage:Photo would grant it (CASL manage = any action).
    'export:Batch',
  ],
};

type AbilityRule = SubjectRawRule<AppAction, AppSubject, MongoQuery>;

/**
 * Construit un objet CASL Ability depuis un rôle.
 * Partagé entre backend (JwtAuthGuard) et frontend (AbilityContext).
 */
const ACTIONS = new Set<string>([
  'read',
  'create',
  'update',
  'delete',
  'generate',
  'upload',
  'manage',
  'moderate',
  'export',
]);
// Doublon assumé du type `AppSubject` : c'est la liste que `buildAbility`
// consulte à l'exécution, et une capacité dont le sujet n'y figure pas est
// silencieusement écartée. Ajouter au type sans ajouter ici compile et ne
// marche pas — le compilateur ne relie pas les deux.
const SUBJECTS = new Set<string>([
  'Photo',
  'Card',
  'Template',
  'Side',
  'User',
  'Batch',
  'Audit',
  'Dashboard',
  'all',
]);

function isAction(v: string): v is AppAction {
  return ACTIONS.has(v);
}
function isSubject(v: string): v is AppSubject {
  return SUBJECTS.has(v);
}
function isRole(v: string): v is AppRole {
  return v in ROLE_CAPABILITIES;
}

export function buildAbility(role: string | null): AppAbility {
  const capabilities = role && isRole(role) ? ROLE_CAPABILITIES[role] : [];

  const rules: AbilityRule[] = [];
  for (const cap of capabilities) {
    const colonIdx = cap.indexOf(':');
    if (colonIdx === -1) {
      continue;
    }
    const action = cap.slice(0, colonIdx);
    const subject = cap.slice(colonIdx + 1);
    if (isAction(action) && isSubject(subject)) {
      rules.push({ action, subject });
    }
  }

  return createMongoAbility<[AppAction, AppSubject]>(rules);
}

// ---------------------------------------------------------------------------

const LIQUID_BUILTINS = new Set([
  'forloop',
  'tablerowloop',
  'true',
  'false',
  'nil',
  'null',
  'blank',
  'empty',
]);
// Internal Graph attributes with no template counterpart.
const EXCLUDED_GRAPH_VIRTUAL = new Set([
  'id',
  'userType',
  'userPrincipalName',
  'onPremisesDistinguishedName',
  // Sélectionné pour que la recherche puisse marquer un compte parti, pas pour
  // être imprimé : c'est un booléen, et `ENTRA_CARD_FIELDS` dérivant des clés
  // du schéma, l'oublier ici en faisait une variable liable sur une carte.
  'accountEnabled',
]);

/** Entra attributes a template default may bind to, as `entra:<field>`. */
export const ENTRA_CARD_FIELDS = Object.keys(graphUserSchema.shape).filter(
  (k): k is keyof GraphUser =>
    k in graphUserSchema.shape && !EXCLUDED_GRAPH_VIRTUAL.has(k),
);

/**
 * The only names a template may use without being configured: the two the render
 * worker injects itself. Every other variable — Entra attributes included — has
 * to be bound explicitly, either by a template default (`entra:`/`isee:`/`lucca:`
 * marker, fixed string, `form:`) or by a CSV column.
 *
 * Entra attributes used to live in this set, so `{{ givenName }}` resolved from
 * Graph by name alone. That match was invisible in the template editor and
 * unavailable to any variable *not* named after a Graph attribute — `{{ nom }}`
 * could never reach `surname`. Binding them like every other source costs one
 * line per field in the editor and makes the mapping something you can read.
 */
export const LIQUID_VIRTUAL_FIELDS = new Set(['upn', 'photo']);

/** Extrait les variables Liquid d'un template SVG. */
export function extractLiquidVars(template: string): Set<string> {
  const vars = new Set<string>();
  for (const [, name] of template.matchAll(/\{\{-?\s*(\w+)/g)) {
    if (name) {
      vars.add(name);
    }
  }
  for (const [, name] of template.matchAll(
    /\{%-?\s*(?:if|unless|elsif)\s+(\w+)/g,
  )) {
    if (name) {
      vars.add(name);
    }
  }
  for (const b of LIQUID_BUILTINS) {
    vars.delete(b);
  }
  return vars;
}

/**
 * Sentinel default value marking a template variable as "fill at generation":
 * the operator supplies one value for the whole batch in the generation form,
 * overlaid onto the defaults before enqueue. Unlike the `isee:`/`lucca:` markers
 * it carries no payload — the variable name is its own label (v1).
 */
export const FORM_MARKER = 'form:';

/** True when a default value is the fill-at-generation sentinel. */
export function isFormField(value: string | null | undefined): boolean {
  return value === FORM_MARKER;
}

/**
 * Operator-entered values for a template's `form:` fields, as sent by the
 * generation endpoints. Single source so the multipart parser and the JSON
 * DTOs cannot drift apart on what they accept.
 */
export const formValuesSchema = z.record(z.string(), z.string());

/**
 * Overlay operator-entered form values onto template defaults. Every default
 * flagged {@link isFormField} is replaced with the entered value (blank when
 * absent) so the single value applies to every recipient in the batch. Non-form
 * defaults pass through untouched; keys in `values` without a matching form
 * default are ignored.
 */
export function applyFormValues(
  defaults: Record<string, string>,
  values: Record<string, string> | undefined,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(defaults)) {
    out[key] = isFormField(value) ? (values?.[key] ?? '') : value;
  }
  return out;
}

/**
 * Profile-photo formats accepted for card generation, as the canonical file
 * extensions returned by `mime-types` `extension()` (e.g. `image/jpeg` → `jpg`).
 * The renderer (resvg) decodes JPEG and PNG but NOT WebP, so WebP is transcoded
 * to PNG before staging. Any other type is rejected (`photo_type_unsupported`).
 */
export const SUPPORTED_PHOTO_EXTENSIONS = new Set(['jpg', 'png', 'webp']);

export interface CsvRow {
  upn: string;
  [key: string]: string;
}

// ---------------------------------------------------------------------------
// Card generation — two-phase pipeline
//
// Phase 1 (resolve / pre-flight): one `CardResolveJobData` per recipient. The
// resolve worker fetches the source record (ISEE/Lucca) + the Entra user +
// photo, validates, and produces a `ResolvedCardEntry`. Phase 2 (render): one
// `CardRenderJobData` per *valid* entry — a fully-resolved data object the
// render worker just templates and rasterises, with no external fetching.
// ---------------------------------------------------------------------------

/**
 * Which card sides to generate. Resolved at preflight time: `front`/`back`
 * collapse the batch to a single-sided one before any job is enqueued, so
 * the render/finalize pipeline only ever sees "has a back" or "doesn't".
 */
export const cardSidesSchema = z.enum(['both', 'front', 'back']);
export type CardSides = z.infer<typeof cardSidesSchema>;

/** Fields shared by every per-card job (both phases), regardless of source. */
interface CardJobBase {
  templateKey: string;
  backTemplateKey?: string;
  bucket: string;
  batchId: string;
  total: number;
}

/** Resolve a card from a CSV row (upn + arbitrary columns, defaults merged in). */
export interface CardResolveCsvJobData extends CardJobBase {
  type: 'csv';
  row: CsvRow;
}

/**
 * Resolve a card from an ISEE student: identified by `id` (login) for the given
 * academic `year`. Template `defaults` (including `isee:` markers) are resolved
 * per student by the resolve worker.
 */
export interface CardResolveIseeJobData extends CardJobBase {
  type: 'isee';
  id: string;
  year: string;
  defaults?: Record<string, string>;
}

/**
 * Resolve a card from a Lucca employee: identified by `id` (login). The employee
 * record is resolved **once per batch** at enqueue time (Lucca has no by-id
 * endpoint, so it's a single roster sweep) and travels on the job — `null` means
 * no roster match. Template `defaults` (including `lucca:` markers) are then
 * resolved per employee without the worker touching Lucca.
 */
export interface CardResolveLuccaJobData extends CardJobBase {
  type: 'lucca';
  id: string;
  defaults?: Record<string, string>;
  employee: Record<string, unknown> | null;
}

/** Per-recipient job for the resolve (pre-flight) phase. */
export type CardResolveJobData =
  CardResolveCsvJobData | CardResolveIseeJobData | CardResolveLuccaJobData;

/**
 * The master list of card data sources — derived from the resolve-job union so
 * it can never drift. This is the single source of truth: add a
 * `CardResolve*JobData` variant and `CardSource` grows automatically, which
 * makes every `Record<CardSource, …>` map and exhaustive `switch` fail to
 * compile until the new source is handled.
 */
export type CardSource = CardResolveJobData['type'];

/**
 * Sources backed by a fetched external record addressed with `source:field`
 * markers (e.g. `isee:promo`) and formatted by a per-source field formatter.
 * `csv` is excluded — its columns are the values directly. Kept as a runtime
 * list (with `satisfies` guarding every entry against {@link CardSource}) so the
 * marker regex and formatter map can derive from it.
 */
const EXTERNAL_CARD_SOURCES = [
  'isee',
  'lucca',
] as const satisfies readonly CardSource[];
export type ExternalCardSource = (typeof EXTERNAL_CARD_SOURCES)[number];

/**
 * Entra is a *field* source but never a *batch* source: a card is generated from
 * a CSV, ISEE or Lucca roster, and the Entra user is looked up for every one of
 * them whatever the batch source. So it addresses fields with the same
 * `source:field` marker syntax without being a {@link CardSource}, and an
 * `entra:` marker resolves on all three paths.
 */
export const ENTRA_FIELD_SOURCE = 'entra';

/** Every source a template default may bind a variable to. */
const TEMPLATE_FIELD_SOURCES = [
  ENTRA_FIELD_SOURCE,
  ...EXTERNAL_CARD_SOURCES,
] as const;
type TemplateFieldSource = (typeof TEMPLATE_FIELD_SOURCES)[number];

const FIELD_MARKER_RE = new RegExp(
  `^(${TEMPLATE_FIELD_SOURCES.join('|')}):(.+)$`,
);

/**
 * Parse a template default into the source field it binds to, or `null` when it
 * is a plain fixed value. Shared by the resolver and the template editor so the
 * two never disagree on what counts as a marker.
 */
export function parseFieldMarker(
  value: string | null | undefined,
): { source: TemplateFieldSource; field: string } | null {
  const m = value ? FIELD_MARKER_RE.exec(value) : null;
  const [, source, field] = m ?? [];
  return field && isOneOf(TEMPLATE_FIELD_SOURCES, source)
    ? { source, field }
    : null;
}

/**
 * Per-card job for the render phase. Carries a fully-resolved `data` object
 * (all Liquid vars, incl. Graph fields, as strings) and an optional S3 key for
 * the recipient's photo. The render worker performs no external fetching.
 */
export interface CardRenderJobData extends CardJobBase {
  upn: string;
  displayName?: string;
  data: Record<string, string>;
  photoKey?: string;
  /**
   * This card's place in the assembled output, when the batch asked to be
   * ordered by a field. Computed at commit, where the resolved values still
   * are, and handed back on the `CardResult` — the rendered cards return
   * through an unordered Redis hash, so the position has to travel with the
   * card that owns it. Absent means the batch asked for no ordering.
   */
  sortRank?: number;
}

/** Status of a resolved recipient: renderable, or excluded (with a reason). */
const resolvedCardStatusSchema = z.enum(['valid', 'error', 'not_found']);
export type ResolvedCardStatus = z.infer<typeof resolvedCardStatusSchema>;

/**
 * Stable code for a known per-card failure. The frontend translates it (via
 * `messageParams` for the dynamic bits); `message` stays as a fallback for
 * arbitrary/upstream errors that have no code.
 */
const cardMessageCodeSchema = z.enum([
  'guest',
  'account_not_found',
  'photo_missing',
  'photo_type_unsupported',
  'missing_fields',
  'unresolved_vars',
  'lucca_not_found',
  'not_enrolled',
]);
export type CardMessageCode = z.infer<typeof cardMessageCodeSchema>;

/** i18n code + interpolation params carried alongside a fallback `message`. */
const messageCodeFields = {
  message: z.string().optional(),
  messageCode: cardMessageCodeSchema.optional(),
  messageParams: z.record(z.string(), z.string()).optional(),
};

/**
 * Outcome of resolving one recipient in the pre-flight phase. Persisted to Redis
 * so the commit step can enqueue render jobs for the `valid` entries and the
 * frontend can show a recap of the excluded ones.
 */
export const resolvedCardEntrySchema = z.object({
  upn: z.string(),
  displayName: z.string().optional(),
  status: resolvedCardStatusSchema,
  ...messageCodeFields,
  data: z.record(z.string(), z.string()).optional(),
  photoKey: z.string().optional(),
});
export type ResolvedCardEntry = z.infer<typeof resolvedCardEntrySchema>;

/** A recipient excluded from generation, with the reason it failed pre-flight. */
const cardRecapErrorSchema = z.object({
  upn: z.string(),
  displayName: z.string().optional(),
  status: resolvedCardStatusSchema,
  ...messageCodeFields,
});
export type CardRecapError = z.infer<typeof cardRecapErrorSchema>;

/** Pre-flight recap shown before the user confirms generation. */
export const cardPreflightRecapSchema = z.object({
  total: z.number(),
  valid: z.array(
    z.object({ upn: z.string(), displayName: z.string().optional() }),
  ),
  errors: z.array(cardRecapErrorSchema),
});
export type CardPreflightRecap = z.infer<typeof cardPreflightRecapSchema>;

/** Parent job of the resolve flow — aggregates children into a manifest + recap. */
export interface CardResolveFinalizeJobData {
  bucket: string;
  batchId: string;
  total: number;
}

export interface CardFinalizeJobData {
  bucket: string;
  batchId: string;
  total: number;
  templateSlug: string;
  hasBack: boolean;
  outputFormat: 'zip' | 'pdf';
}

/** One rendered card handed to the output-assembly job, addressed by S3 key(s). */
export interface CardOutputCard {
  upn: string;
  s3Key: string;
  backS3Key?: string;
}

/**
 * Job data for the `cards-output` queue — the zip/pdf assembly split out of
 * finalize. Self-contained on purpose: the card keys ride in the job itself,
 * so a retry (or a stalled re-run on another worker) needs no Redis `batch:*`
 * state that may have expired.
 */
export interface CardOutputJobData {
  batchId: string;
  templateSlug: string;
  hasBack: boolean;
  outputFormat: 'zip' | 'pdf';
  cards: CardOutputCard[];
}

export const cardResultSchema = z.object({
  upn: z.string(),
  displayName: z.string().optional(),
  status: z.enum(['success', 'error', 'not_found']),
  s3Key: z.string().optional(),
  backS3Key: z.string().optional(),
  /** Echo of {@link CardRenderJobData.sortRank} — the card's place in the output. */
  sortRank: z.number().optional(),
  ...messageCodeFields,
});
export type CardResult = z.infer<typeof cardResultSchema>;

/** Statut par résultat dans la map finale d'un batch (différent du status CardResult). */
export type BatchResultStatus = 'ok' | 'notFound' | 'error';

export interface CardProgressData {
  done: number;
  total: number;
  current?: string;
  result?: CardResult;
}

// ---------------------------------------------------------------------------
// API response schemas — the source the backend's OpenAPI DTOs are built from
//
// These are read by backend/src/**/dto only. The frontend
// does NOT import them: it consumes their generated twin (hey-api reads the
// openapi.json these produce, and emits both the types and the response
// validators). So they are upstream of the generated client, not a copy of it
// — and the inferred `z.infer` aliases that used to sit beside each one are
// gone, because that is the half the generated types now cover.
// ---------------------------------------------------------------------------

/**
 * Dates are ISO-8601 strings over JSON. Handlers return Prisma `Date` objects,
 * and the backend's StandardSchemaSerializerInterceptor parses responses through these
 * schemas *before* JSON.stringify runs — so the schema itself must accept a
 * `Date` and encode it. `z.preprocess` is the one construct whose JSON-schema
 * projection is a plain `string` on both the input and output side (a
 * union/transform/codec made nestjs-zod's OpenAPI generation throw on the
 * unrepresentable `z.date()`; not re-tried since the move to zod-openapi).
 */
const dateLikeSchema = z
  .preprocess((v) => (v instanceof Date ? v.toISOString() : v), z.string())
  // Without this, the transform's `unknown` input side marks every date
  // property optional in the generated OpenAPI document (`submittedAt?:`),
  // even though the runtime parse rejects undefined.
  .nonoptional();

// ── /admin/me ───────────────────────────────────────────────────────────────
export const adminMeSchema = z.object({
  oid: z.string(),
  upn: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  // Required-nullable, not optional: AuthUser.role is `string | null` and the
  // controller always includes it — clients branch on it without guessing.
  role: z.string().nullable(),
});

// ── AppUser (Prisma row) ────────────────────────────────────────────────────
export const appUserSchema = z.object({
  id: z.string(),
  oid: z.string(),
  upn: z.string().nullable(),
  displayName: z.string().nullable(),
  role: z.string().nullable(),
  active: z.boolean(),
  externalId: z.string().nullable().optional(),
  createdAt: dateLikeSchema,
  updatedAt: dateLikeSchema,
});

export const appUserSortFields = [
  'displayName',
  'upn',
  'role',
  'active',
  'createdAt',
  'updatedAt',
] as const;
export type AppUserSortField = (typeof appUserSortFields)[number];

export const appUsersPageSchema = z.object({
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  users: z.array(appUserSchema),
});

// ── AuditLog ────────────────────────────────────────────────────────────────
const auditLogSchema = z.object({
  id: z.number(),
  actorOid: z.string(),
  actorName: z.string().nullable(),
  actorUpn: z.string().nullable(),
  action: z.string(),
  targetId: z.string().nullable(),
  details: z.record(z.string(), z.unknown()).nullable(),
  ip: z.string().nullable(),
  createdAt: dateLikeSchema,
});

export const auditLogsPageSchema = z.object({
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  logs: z.array(auditLogSchema),
});

// ── CardSide ────────────────────────────────────────────────────────────────
export const cardSideSchema = z.object({
  id: z.string(),
  name: z.string(),
  variables: z.array(z.string()),
  svgUrl: z.string().optional(),
  createdBy: z.string(),
  createdAt: dateLikeSchema,
  updatedAt: dateLikeSchema.optional(),
});

export const svgBodySchema = z.object({ svg: z.string() });

export const deletedFlagSchema = z.object({ deleted: z.boolean() });

// ── CardTemplate ────────────────────────────────────────────────────────────
const sideRefSchema = z.object({ id: z.string(), name: z.string() }).nullable();

/** Template kind — drives the card-generation source: ISEE / Lucca / none. */
export const templateTypeSchema = z.enum(['student', 'employee', 'other']);
export type TemplateType = z.infer<typeof templateTypeSchema>;

export const cardTemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  active: z.boolean(),
  type: templateTypeSchema,
  createdBy: z.string(),
  frontSideId: z.string().nullable(),
  backSideId: z.string().nullable(),
  defaults: z.record(z.string(), z.string()).nullable(),
  frontSide: sideRefSchema,
  backSide: sideRefSchema,
  createdAt: dateLikeSchema,
  updatedAt: dateLikeSchema,
});

export const cardTemplateSvgsSchema = z.object({
  frontSvg: z.string(),
  name: z.string(),
  backSvg: z.string().nullable(),
  defaults: z.record(z.string(), z.string()),
});

// ── Dashboard ───────────────────────────────────────────────────────────────
export const dashboardSchema = z.object({
  services: z.object({
    db: z.enum(['ok', 'error']),
    redis: z.enum(['ok', 'error']),
    s3: z.enum(['ok', 'error']),
    worker: z.enum(['ok', 'idle']),
    workerCount: z.number(),
  }),
  queue: z.object({
    generate: z.record(z.string(), z.number()).nullable(),
    finalize: z.record(z.string(), z.number()).nullable(),
  }),
  stats: z.object({
    users: z.number().nullable(),
    templates: z.number().nullable(),
  }),
});

// ── Cards: batch / history / jobs ───────────────────────────────────────────
const batchStatsSchema = z.object({
  success: z.number(),
  error: z.number(),
  notFound: z.number(),
});

const batchResultsMapSchema = z.record(
  z.string(),
  z.enum(['ok', 'notFound', 'error']),
);

export const cardBatchSchema = z.object({
  id: z.string(),
  jobId: z.string(),
  actorOid: z.string(),
  actorUpn: z.string().nullable(),
  actorName: z.string().nullable(),
  templateName: z.string(),
  outputFormat: z.string(),
  rowCount: z.number(),
  identifiers: z.array(z.string()),
  s3Key: z.string().nullable().optional(),
  results: batchResultsMapSchema.nullable().optional(),
  expiresAt: dateLikeSchema.nullable(),
  createdAt: dateLikeSchema,
  completedAt: dateLikeSchema.nullable().optional(),
  stats: batchStatsSchema.nullable().optional(),
  /** Backend-computed lifecycle state; the client must not infer this itself. */
  status: z.enum(['processing', 'done', 'failed']).optional(),
  failedReason: z.string().nullable().optional(),
});

export const generateCardsResponseSchema = z.object({
  jobId: z.string(),
  deduplicated: z.boolean(),
});

/** Response of the pre-flight endpoints: the id to stream/recap/commit against. */
export const preflightResponseSchema = z.object({
  resolveBatchId: z.string(),
});

/** Body of POST /cards/commit — turn a resolved batch into a render batch. */
export const commitCardsRequestSchema = z.object({
  resolveBatchId: z.string().min(1),
  outputFormat: z.enum(['zip', 'pdf']).optional(),
});

export const cardJobSummarySchema = z.object({
  jobId: z.string(),
  createdAt: z.number(),
  total: z.number(),
  done: z.number(),
  /** `review`: resolve-only — the pre-flight recap awaits the user's decision. */
  status: z.enum(['queued', 'processing', 'review', 'done', 'error']),
  /** Pre-flight (resolve) batch, resumable at /cards?resume=<jobId>, or render batch. */
  kind: z.enum(['resolve', 'render']),
  downloadAvailable: z.boolean(),
  queuePosition: z.number().nullable(),
});

export const cardDownloadUrlSchema = z.object({ url: z.string() });

/** Presigned URLs for previewing a single generated card (front + optional back). */
export const cardPreviewUrlSchema = z.object({
  url: z.string(),
  backUrl: z.string().optional(),
});

/**
 * Presigned preview URLs for a whole batch, keyed by upn — one request for the
 * results table instead of one per card (a large batch would trip the API's
 * own inbound rate limit).
 */
export const cardPreviewUrlsBulkSchema = z.object({
  previews: z.record(z.string(), cardPreviewUrlSchema),
});

// ── GET /cards/stream/:jobId (text/event-stream) ────────────────────────────
// One schema per SSE event name; the `event:` line is not expressible in
// OpenAPI 3.0, so each payload says in its description which name carries it.

export const cardStreamProgressSchema = z
  .object({
    done: z.number(),
    total: z.number(),
    current: z.string().optional(),
    result: cardResultSchema.optional(),
  })
  .meta({
    description:
      '`event: progress` — one card processed, or a bar-sync (no `result`).',
  });

export const cardStreamThrottledSchema = z
  .object({ service: z.string(), resumeAt: z.number() })
  .meta({
    description:
      '`event: throttled` — an upstream 429 paused the batch until `resumeAt` (epoch ms). Not terminal.',
  });

export const cardStreamFinalizingSchema = z.object({}).meta({
  description:
    '`event: finalizing` — every card is rendered, the batch is assembling its zip/pdf. Not terminal.',
});

export const cardStreamDoneSchema = z
  .object({ jobId: z.string() })
  .meta({ description: '`event: done` — the batch finished. Terminal.' });

export const cardStreamMessageSchema = z.object({ message: z.string() }).meta({
  description:
    '`event: error` (the batch failed, terminal) or `event: timeout` (the server capped the stream lifetime — reconnect).',
});

/**
 * Event name → payload schema: what the backend's typed `send()` checks
 * against, so an event can only carry the payload the document declares.
 */
export const cardStreamEvents = {
  progress: cardStreamProgressSchema,
  throttled: cardStreamThrottledSchema,
  finalizing: cardStreamFinalizingSchema,
  done: cardStreamDoneSchema,
  error: cardStreamMessageSchema,
  timeout: cardStreamMessageSchema,
} as const;

// ---------------------------------------------------------------------------
// ISEE — sessions tree & student lookup
// ---------------------------------------------------------------------------

/** Whether the ISEE integration is configured (both env vars set) — a UI feature flag. */
export const iseeStatusSchema = z.object({ enabled: z.boolean() });
export type IseeStatus = z.infer<typeof iseeStatusSchema>;

/** A session ISEE rebuilt from its enrollments: these five fields find its members. */
export const iseeSessionSchema = z.object({
  school: z.string(),
  program: z.string(),
  promo: z.string(),
  campus: z.string(),
  section: z.string(),
});
export type IseeSession = z.infer<typeof iseeSessionSchema>;

/** A campus leaf of the sessions tree: one session per intake of the promo. */
const iseeSessionGroupSchema = z.object({
  admStatus: z.array(z.string()),
  speciality: z.array(z.string()),
  sessions: z.array(iseeSessionSchema),
});

/**
 * The sessions running from today to two months ahead, as ISEE nests them:
 * school → program → cohort (`<current section> <promo>`) → campus → leaf.
 */
export const iseeSessionTreeSchema = z.record(
  z.string(),
  z.record(
    z.string(),
    z.record(z.string(), z.record(z.string(), iseeSessionGroupSchema)),
  ),
);
export type IseeSessionTree = z.infer<typeof iseeSessionTreeSchema>;

/**
 * A node picked in the sessions tree, down to a cohort; no campus = all of
 * them. `admStatus`, `section` and `speciality` are comma-separated lists
 * narrowing it (some cards are for apprentices only); absent = all of them.
 */
export const iseeSessionQuerySchema = z.object({
  school: z.string().min(1),
  program: z.string().min(1),
  cohort: z.string().min(1),
  campus: z.string().optional(),
  admStatus: z.string().optional(),
  section: z.string().optional(),
  speciality: z.string().optional(),
});
export type IseeSessionQuery = z.infer<typeof iseeSessionQuerySchema>;

/** A display field that ISEE may return as null/absent — coerced to ''. */
const iseeText = z
  .string()
  .nullish()
  .transform((v) => v ?? '');

/** A single enrollment as shown in the preview (subset of ISEE's Enrollment). */
export const iseeStudentSchema = z.object({
  login: z.string(),
  firstname: iseeText,
  lastname: iseeText,
  section: iseeText,
  campus: iseeText,
  promo: iseeText,
  admStatus: iseeText,
  speciality: iseeText,
  /** Scholar year of the enrollment: the only year ISEE serves its detail for. */
  year: iseeText,
});
export type IseeStudent = z.infer<typeof iseeStudentSchema>;

/**
 * What the API actually emits for a student — the tolerant `iseeStudentSchema`
 * above exists to *parse* ISEE's null-mixed upstream payload; by the time the
 * controller answers, every field is a plain string. The response DTO uses
 * this strict shape so the OpenAPI document (and the client types generated
 * from it) describe the wire, not the parser's tolerance.
 */
const iseeStudentWireSchema = z.object({
  login: z.string(),
  firstname: z.string(),
  lastname: z.string(),
  section: z.string(),
  campus: z.string(),
  promo: z.string(),
  admStatus: z.string(),
  speciality: z.string(),
  year: z.string(),
});

export const iseeStudentsResponseSchema = z.object({
  count: z.number(),
  students: z.array(iseeStudentWireSchema),
});
export type IseeStudentsResponse = z.infer<typeof iseeStudentsResponseSchema>;

/** A selectable ISEE Enrollment field a template default can be linked to. */
const iseeFieldSchema = z.object({
  key: z.string(),
  type: z.enum(['string', 'boolean', 'integer', 'number']),
  format: z.string().optional(),
});

export const iseeFieldsResponseSchema = z.object({
  fields: z.array(iseeFieldSchema),
});
export type IseeFieldsResponse = z.infer<typeof iseeFieldsResponseSchema>;

// ---------------------------------------------------------------------------
// Lucca — employee directory (feature flag + linkable fields)
// ---------------------------------------------------------------------------

/** Whether the Lucca integration is configured (both env vars set) — a UI feature flag. */
export const luccaStatusSchema = z.object({ enabled: z.boolean() });
export type LuccaStatus = z.infer<typeof luccaStatusSchema>;

/** Selectable Lucca employee fields (same shape as ISEE fields). */
export const luccaFieldsResponseSchema = z.object({
  fields: z.array(iseeFieldSchema),
});
export type LuccaFieldsResponse = z.infer<typeof luccaFieldsResponseSchema>;

// ── Batch (photo bulk upload) ───────────────────────────────────────────────
export const batchValidationResultSchema = z.object({
  upn: z.string(),
  found: z.boolean(),
  displayName: z.string().optional(),
  userId: z.string().optional(),
  onPremisesDistinguishedName: z.string().nullable().optional(),
  /**
   * Target exists in the Lucca roster (an employee) — their photo goes through
   * moderation instead of a direct upload. Also `true` when Lucca is disabled
   * or unreachable (fail closed). Only set when `found` is true.
   */
  isEmployee: z.boolean().optional(),
});

export const batchUploadResultSchema = z.object({
  filename: z.string(),
  login: z.string(),
  userId: z.string().optional(),
  displayName: z.string().optional(),
  status: z.enum(['success', 'pending', 'not_found', 'skipped', 'error']),
  message: z.string().optional(),
  /**
   * Graph's own `error.code` when Graph is what refused, e.g.
   * `InvalidImageDimensions`. Carried beside `message` because the message is
   * often a .NET `ToString()` — "Exception of type '…' was thrown." — with the
   * code as its only informative part, and because Microsoft's error reference
   * is explicit that `code` is the field to branch on. The UI translates the
   * codes it knows and keeps the raw message underneath for the ticket.
   */
  code: z.string().optional(),
  pendingId: z.string().optional(),
});

// ── Users: photo moderation ─────────────────────────────────────────────────
export const pendingPhotoSchema = z.object({
  id: z.string(),
  userOid: z.string(),
  userUpn: z.string().nullable(),
  userName: z.string().nullable(),
  submittedAt: dateLikeSchema,
  mimeType: z.string(),
  sizeBytes: z.number(),
  thumbnailUrl: z.string(),
  rejectedAt: dateLikeSchema.nullable().optional(),
  rejectionReason: z.string().nullable().optional(),
  submittedByOid: z.string().nullable().optional(),
  submittedByUpn: z.string().nullable().optional(),
  submittedByName: z.string().nullable().optional(),
});

/**
 * `GET /users/me/pending` and `GET /users/:userId/pending` — "nothing awaiting
 * moderation" is a normal answer, carried as `pending: null`. The nullability
 * lives on a property, never on the response root: a handler returning a bare
 * `null` makes Nest send an *empty* body (not JSON `null`), which the generated
 * hey-api client reads back as `{}` — a truthy phantom row.
 */
export const pendingPhotoResponseSchema = z.object({
  pending: pendingPhotoSchema.nullable(),
});

/**
 * POST /users/:userId/photo response.
 * Either: { status: 'pending', pendingId } (user→self route, requires moderation)
 * Or:     { message: 'Photo uploaded successfully' } (privileged route).
 * Modelled as a permissive object so OpenAPI emits a single schema.
 */
export const photoUploadAckSchema = z.object({
  status: z.literal('pending').optional(),
  pendingId: z.string().optional(),
  message: z.string().optional(),
});

export const messageSchema = z.object({ message: z.string() });

export const sweepAckSchema = z.object({
  jobId: z.string(),
  message: z.string(),
});

// ── Health ──────────────────────────────────────────────────────────────────
export const healthSchema = z.object({
  status: z.enum(['ok', 'degraded']),
  checks: z.record(z.string(), z.enum(['ok', 'error'])),
});

// ---------------------------------------------------------------------------
// Audit
// ---------------------------------------------------------------------------

export const AUDIT_ACTIONS = {
  PHOTO_UPLOAD: 'photo.upload',
  PHOTO_DELETE: 'photo.delete',
  PHOTO_PENDING_SUBMIT: 'photo.pending.submit',
  PHOTO_PENDING_SUBMIT_FOR: 'photo.pending.submit_for',
  PHOTO_PENDING_APPROVE: 'photo.pending.approve',
  PHOTO_PENDING_REJECT: 'photo.pending.reject',
  PHOTO_PENDING_CANCEL: 'photo.pending.cancel',
  PHOTO_PENDING_SWEEP: 'photo.pending.sweep',
  PHOTO_EXPORT: 'photo.export',
  BATCH_UPLOAD: 'batch.upload',
  CARD_GENERATE: 'card.generate',
  CARD_PREFLIGHT_DISCARD: 'card.preflight.discard',
  CARD_RENDER_CANCEL: 'card.render.cancel',
  ADMIN_USER_CREATE: 'admin.user.create',
  ADMIN_USER_UPDATE: 'admin.user.update',
  ADMIN_SIDE_CREATE: 'admin.side.create',
  ADMIN_SIDE_RENAME: 'admin.side.rename',
  ADMIN_SIDE_DELETE: 'admin.side.delete',
  ADMIN_TEMPLATE_CREATE: 'admin.template.create',
  ADMIN_TEMPLATE_UPDATE: 'admin.template.update',
  ADMIN_TEMPLATE_DELETE: 'admin.template.delete',
} as const;

// ---------------------------------------------------------------------------
// API error codes
// ---------------------------------------------------------------------------

/**
 * Codes machine portés par le corps d'une erreur HTTP, à côté de la prose.
 * `HttpExceptionFilter` les recopie explicitement dans la réponse et le client
 * branche dessus : le statut seul ne distingue pas deux refus, et le message
 * qui l'accompagne est écrit pour un humain — en anglais, non traduit.
 */
export const API_ERROR_CODES = {
  /** Suppression de la photo d'un salarié tentée par un non-admin. */
  EMPLOYEE_DELETE_ADMIN_ONLY: 'employee_delete_admin_only',
} as const;

// ---------------------------------------------------------------------------
// Upstream throttling
// ---------------------------------------------------------------------------

/** Foreign services whose 429s are escalated as {@link ThrottledError}. */
export type ThrottledService = 'graph' | 'isee' | 'lucca';

/**
 * A foreign service kept answering 429 past the client's in-process retries.
 * Carries the service and the raw Retry-After so the consumer can decide the
 * wait itself — the worker pauses the dependent resolve queues for it, the API
 * surfaces it as an HTTP 429 with a Retry-After header.
 */
export class ThrottledError extends Error {
  /** Duck-typing marker — see {@link isThrottledError}. */
  readonly isThrottledError = true as const;

  constructor(
    readonly service: ThrottledService,
    /** Retry-After from the final 429 in ms, uncapped; null when absent. */
    readonly retryAfterMs: number | null,
    message: string,
  ) {
    super(message);
    this.name = 'ThrottledError';
  }
}

/**
 * Duck-typed on the marker rather than `instanceof`: the class is compiled
 * into each package's dist, so two copies can coexist at runtime.
 */
export function isThrottledError(err: unknown): err is ThrottledError {
  return (
    typeof err === 'object' &&
    err !== null &&
    'isThrottledError' in err &&
    err.isThrottledError === true
  );
}

// ---------------------------------------------------------------------------
// Upstream rejection
// ---------------------------------------------------------------------------

/**
 * Graph refused a request and said why. Graph answers every failure with
 * `{"error":{"code","message","innerError"}}` and that message is written to be
 * read — "The image format is not supported", not a status number. Wrapping it
 * in a plain `Error` sent it to the log and to Sentry and left the client with
 * `HttpExceptionFilter`'s generic 500, which is how #543 became forty
 * indistinguishable failures with nothing to act on at either end.
 *
 * Carries the parts separately so the API can answer with Graph's own status
 * and put `code` in the response body — the Graph error reference is explicit
 * that `code` is the field to branch on and `message` the one to show, never
 * the reverse.
 */
export class GraphRejectedError extends Error {
  /** Duck-typing marker — see {@link isGraphRejectedError}. */
  readonly isGraphRejectedError = true as const;

  constructor(
    /** The HTTP status Graph answered with. */
    readonly status: number,
    /** Graph's `error.code`, e.g. `ImageNotSupported`. Null if it sent none. */
    readonly graphCode: string | null,
    /** Graph's `error.message`, already human-readable. */
    readonly graphMessage: string | null,
    /** `innerError['request-id']` — what Microsoft support asks for. */
    readonly requestId: string | null,
    message: string,
  ) {
    super(message);
    this.name = 'GraphRejectedError';
  }
}

/** Duck-typed for the same reason as {@link isThrottledError}. */
export function isGraphRejectedError(err: unknown): err is GraphRejectedError {
  return (
    typeof err === 'object' &&
    err !== null &&
    'isGraphRejectedError' in err &&
    err.isGraphRejectedError === true
  );
}

/**
 * Parse an HTTP Retry-After header (integer seconds or HTTP-date) to
 * milliseconds, uncapped. Returns null for an absent or unparseable value.
 */
export function parseRetryAfterMs(retryAfter: string | null): number | null {
  if (retryAfter === null || retryAfter.length === 0) {
    return null;
  }
  const seconds = Number(retryAfter);
  if (Number.isInteger(seconds) && seconds >= 0) {
    return seconds * 1000;
  }
  const dateMs = Date.parse(retryAfter);
  if (Number.isNaN(dateMs)) {
    return null;
  }
  return Math.max(0, dateMs - Date.now());
}

/**
 * Render every header of a response for throttle diagnostics, sorted for
 * stable logs. Response headers only — never pass request headers here (they
 * carry Authorization).
 */
export function formatResponseHeaders(headers: Headers): string {
  return JSON.stringify(
    Object.fromEntries(
      [...headers.entries()].sort(([a], [b]) => a.localeCompare(b)),
    ),
  );
}
